import apiClient from "./apiClient";
import axios from "axios";
import { apiUrl } from "./ApplicantAPIService";

// ─── Helper: extract the most useful error message from an Axios error ──────
const extractBackendMessage = (error) => {
  // Try the response body first (backend may return a string or { message: "..." })
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data.trim();
  if (data?.message) return data.message;
  if (data?.error) return data.error;
  // HTTP status-level fallbacks
  const status = error?.response?.status;
  if (status === 429) return "Too many requests. Please wait a moment and try again.";
  if (status === 503 || status === 502) return "Server is currently unavailable. Please try again shortly.";
  if (status === 401) return "Unauthorized. Please log in again.";
  if (status === 500) return "Internal server error. Please try again.";
  // Network / timeout
  if (error?.code === "ECONNREFUSED" || error?.message?.includes("Network Error"))
    return "Cannot connect to the server. Please check your connection.";
  if (error?.code === "ECONNABORTED") return "Request timed out. Please try again.";
  return error?.message || "An unexpected error occurred.";
};

// Create Flash Test & Generate Questions
export const generateFlashTest = async (payload, recruiterId) => {
  let colorScheme = parseInt(payload.colorScheme);
  if (isNaN(colorScheme) || colorScheme < 1 || colorScheme > 7) {
    colorScheme = 5;
  }

  const backendPayload = {
    examName: payload.examName,
    subject: payload.subject,
    category: payload.category,
    difficulty: payload.difficulty,
    questionCount: parseInt(payload.questionCount) || 5,
    duration: parseInt(payload.duration) || 0,
    subTopics: payload.subTopics || [],  // fallback camelCase key
    colorScheme: colorScheme
  };


  try {
    const jwtToken = localStorage.getItem("jwtToken");
    const headers = {
      "Content-Type": "application/json"
    };
    if (jwtToken) {
      headers["Authorization"] = `Bearer ${jwtToken}`;
    }

    // Use plain axios with Authorization header — the create endpoint requires a JWT token,
    // but using plain axios avoids apiClient's automatic redirect on 401.
    return await axios.post(
      `${apiUrl}/test/createTest/${recruiterId || 3}`,
      backendPayload,
      { headers, timeout: 120000 }
    );
  } catch (error) {
    // Re-throw with the real backend message so UI can display it
    const msg = extractBackendMessage(error);
    const friendlyError = new Error(msg);
    friendlyError.backendMessage = msg;
    throw friendlyError;
  }
};

// Display Questions
export const getFlashTestQuestions = async (examId) => {
  const token = localStorage.getItem("jwtToken");
  return await apiClient.get(`/test/questions-display/${examId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

// Add Question
export const addFlashTestQuestion = async (payload) => {
  return await apiClient.post("/test/add-question", payload);
};

// Update Question
export const updateFlashTestQuestion = async (payload) => {
  return await apiClient.put("/test/update-question", payload);
};

// Delete Question
export const deleteFlashTestQuestion = async (payload) => {
  return await apiClient.delete("/test/delete-question", { data: payload });
};

// Submit Answer
export const submitFlashTestAnswer = async (payload) => {
  return await apiClient.post("/test/submission", payload);
};

// Get Result
export const getFlashTestResult = async (examId, applicantId) => {
  return await apiClient.get(
    `/test/testmoz-result/${examId}/${applicantId}`
  );
};

// Get Recruiter Exams
export const getRecruiterExams = async () => {
  return await apiClient.get(`/test/recruiter/exams`);
};

// Get All Results for an Exam
export const getFlashTestAllResults = async (examId) => {
  return await apiClient.get(`/test/students/${examId}`);
};

// Submit Exam Answers and Update Score
export const submitFlashTestExam = async (examId, payload) => {
  return await apiClient.post(`/test/submit/${examId}`, payload);
};

// Delete Exam
export const deleteFlashTest = async (examId) => {
  return await apiClient.delete(`/test/delete-test/${examId}`);
};

// Start Test (Record start time)
export const saveStudentStartTime = async (payload) => {
  return await apiClient.put("/test/student/start-time", payload);
};

// Fetch Test questions and details without answers (Public start endpoint)
export const startFlashTest = async (examId) => {
  return await axios.get(`${apiUrl}/test/start/${examId}`, {
    headers: { "Content-Type": "application/json" },
    timeout: 10000
  });
};

// Update Test Status (Open/Close)
export const updateFlashTestStatus = async (examId, isActive) => {
  return await apiClient.put(`/test/update-status/${examId}`, { isActive });
};

// Check Test Status
export const checkFlashTestStatus = async (examId) => {
  return await axios.get(`${apiUrl}/test/exam/${examId}`, {
    headers: { "Content-Type": "application/json" }
  });
};

// Update Show Answers Status
export const updateFlashTestShowAnswers = async (examId, showAnswers) => {
  return await apiClient.put(`/test/update-show-answers/${examId}`, { showAnswers });
};

// Get Show Answers Status
export const getFlashTestShowAnswers = async (examId) => {
  return await axios.get(`${apiUrl}/test/exam-show-answers/${examId}`, {
    headers: { "Content-Type": "application/json" }
  });
};